import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { mediaList, coverOf, coverIndex } from "@/lib/media";
import { MediaGallery } from "@/components/MediaGallery";
import PageLoader from "@/components/PageLoader";

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: s, isLoading, isError } = useQuery({
    queryKey: ["service", slug],
    queryFn: () => api.get(`/services/${slug}`).then((r) => r.data),
  });

  if (isLoading) return <PageLoader />;
  if (isError || !s) return <div className="pt-32 text-center text-white/50">Service not found.</div>;

  const media = mediaList(s);
  const cover = coverOf(s);
  const showGallery = media.length > 1 || cover.kind !== "image";

  return (
    <div data-testid="service-detail-page">
      <section className="relative h-[60vh] min-h-[400px] flex items-end">
        {cover.src ? <img src={cover.src} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
          : cover.video && <video src={`${cover.video}#t=0.5`} muted playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/50 to-black/40" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 pb-10 w-full">
          <Link to="/services" className="inline-flex items-center gap-2 text-white/70 hover:text-[#D4AF37] text-sm mb-5" data-testid="back-to-services">
            <ArrowLeft size={16} /> All Services
          </Link>
          <p className="font-mono-label uppercase tracking-[0.25em] text-xs text-[#D4AF37]">{s.category}</p>
          <h1 className="font-display text-4xl md:text-6xl text-white mt-2">{s.title}</h1>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-14 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-white/70 text-lg leading-relaxed whitespace-pre-line">
            {s.description}
          </motion.p>
          {showGallery && (
            <div className="mt-10" data-testid="service-gallery">
              <p className="font-mono-label uppercase tracking-[0.25em] text-xs text-[#D4AF37] mb-4">Watch & Explore</p>
              <MediaGallery media={media} title={s.title} startIndex={coverIndex(s)} />
            </div>
          )}
        </div>
        <div>
          <div className="glass rounded-2xl p-6 sticky top-24">
            <p className="text-white/50 text-sm">Starting at</p>
            <p className="font-display text-3xl text-[#D4AF37] mt-1">{s.price}</p>
            {s.hourly_rate > 0 && (
              <p className="text-white/50 text-sm flex items-center gap-1.5 mt-2">
                <Clock size={14} /> ${s.hourly_rate} / hour
              </p>
            )}
            <button
              onClick={() => navigate(`/book?service=${s.id}`)}
              data-testid="book-this-service-btn"
              className="btn-gold w-full rounded-full px-6 py-3 mt-6 uppercase tracking-wide font-mono-label text-sm flex items-center justify-center gap-2"
            >
              <Calendar size={16} /> Book This
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
